import { PropsWithChildren } from 'react';
import { Navigate, RouteObject } from 'react-router-dom';
import UserFeaturesGuard from '~/guards/UserFeaturesGuard';
import { useIsBookkeepingConfigurationValid } from '~/services/bookkeeping/bookkeeping-configuration';
import { useVATBooks } from '~/services/bookkeeping/vat-books';
import { useVATReportingFrequency } from '~/services/bookkeeping/vat-reporting-frequency';
import { SectionScopeProvider } from '~/state/sectionScope';
import { FeatureFlag, UserRole } from '~/types/company';
import lazyImport from '~/utils/lazyImport';
import GeneralBookkeepingCashBook from '~/views/bookkeeping/cash-book/GeneralBookkeepingCashBook';
import PayrollRegistration from './non-electronic-documents/PayrollRegistration';

const VATBooksEmptyState = lazyImport(() => import('~/views/bookkeeping/VAT-books/VATBooksEmptyState'));
const VATBooks = lazyImport(() => import('~/views/bookkeeping/VAT-books/VATBooks'));
const WithholdingTaxManagement = lazyImport(
  () => import('~/views/bookkeeping/withholding-tax/WithholdingTaxManagement'),
);
const Intrastat = lazyImport(() => import('~/views/bookkeeping/intrastat/Intrastat'));
const EditIntrastatDeclaration = lazyImport(() => import('~/views/bookkeeping/intrastat/EditIntrastatDeclaration'));
const FinancialStatement = lazyImport(() => import('~/views/bookkeeping/financial-statement/FinancialStatement'));
const VatDeclaration = lazyImport(() => import('~/views/bookkeeping/vat-declaration/VatDeclaration'));
const VatDeclarationDetailPage = lazyImport(
  () => import('~/views/bookkeeping/vat-declaration/VatDeclarationDetailPage'),
);
const VatDeclarationSectionPage = lazyImport(
  () => import('~/views/bookkeeping/vat-declaration/VatDeclarationSectionPage'),
);
const Declaration770 = lazyImport(() => import('~/views/bookkeeping/declaration-770/Declaration770'));
const Declaration770DetailPage = lazyImport(
  () => import('~/views/bookkeeping/declaration-770/Declaration770DetailPage'),
);
const Declaration770SectionPage = lazyImport(
  () => import('~/views/bookkeeping/declaration-770/Declaration770SectionPage'),
);
const CUDeclarations = lazyImport(() => import('~/views/bookkeeping/cu-declarations/CUDeclarations'));
const CUDeclarationDetailPage = lazyImport(() => import('~/views/bookkeeping/cu-declarations/CUDeclarationDetailPage'));
const CUDeclarationFrontespizioPage = lazyImport(
  () => import('~/views/bookkeeping/cu-declarations/forms/CUDeclarationFrontespizioPage'),
);
const IncomeTaxDeclaration = lazyImport(
  () => import('~/views/bookkeeping/income-tax-declaration/IncomeTaxDeclaration'),
);
const IncomeTaxDeclarationDetailPage = lazyImport(
  () => import('~/views/bookkeeping/income-tax-declaration/IncomeTaxDeclarationDetailPage'),
);
const IncomeTaxDeclarationSectionPage = lazyImport(
  () => import('~/views/bookkeeping/income-tax-declaration/IncomeTaxDeclarationSectionPage'),
);
const CUDeclarationRecipientRoute = lazyImport(
  () => import('~/views/bookkeeping/cu-declarations/CUDeclarationRecipientRoute'),
);
const CreateManualRegistration = lazyImport(() => import('~/views/bookkeeping/CreateManualRegistration'));
const CreateVATRegistration = lazyImport(() => import('~/views/bookkeeping/VAT-registration/CreateVATRegistration'));
const EditManualVATRegistration = lazyImport(
  () => import('~/views/bookkeeping/VAT-registration/EditManualVATRegistration'),
);
const EditVATRegistration = lazyImport(() => import('~/views/bookkeeping/VAT-registration/EditVATRegistration'));
const BookkeepingBankTransactions = lazyImport(
  () => import('~/views/bookkeeping/bank-transactions/BookkeepingBankTransactions'),
);
const NonElectronicDocumentsPage = lazyImport(
  () => import('~/views/bookkeeping/non-electronic-documents/NonElectronicDocumentsPage'),
);
const BookkeepingReceiptRegistration = lazyImport(
  () => import('~/views/bookkeeping/documents/BookkeepingReceiptRegistration'),
);

const GeneralBookkeepingLedgerCards = lazyImport(
  () => import('~/views/bookkeeping/ledgers/GeneralBookkeepingLedgerCards'),
);
const ProvisionalBalance = lazyImport(() => import('~/views/bookkeeping/provisional-balance/ProvisionalBalance'));
const FixedAssetsPage = lazyImport(() => import('~/views/bookkeeping/fixed-assets/FixedAssetsPage'));
const VATSettlement = lazyImport(() => import('~/views/bookkeeping/settlement/VATSettlement'));
const LIPE = lazyImport(() => import('~/views/bookkeeping/lipe/LIPE'));
const F24List = lazyImport(() => import('~/views/bookkeeping/taxes/F24List'));
const TaxDebitCredits = lazyImport(() => import('~/views/bookkeeping/taxes/TaxDebitCredits'));
const CreateBillsVATRegistrations = lazyImport(() => import('./VAT-registration/CreateBillsVATRegistrations'));
const EditBillVATRegistration = lazyImport(() => import('./VAT-registration/EditBillVATRegistration'));
const BookkeepingDocuments = lazyImport(() => import('./documents/BookkeepingDocuments'));
const BookkeepingSettings = lazyImport(() => import('~/views/bookkeeping/settings/BookkeepingSettings'));

interface BookkeepingGuardProps extends PropsWithChildren {
  fallback?: boolean;
}

const BookkeepingGuard: React.FC<BookkeepingGuardProps> = ({ children, fallback }) => {
  const { data: books } = useVATBooks();
  const currentYear = new Date().getFullYear();
  const { data: reportingFrequencies } = useVATReportingFrequency(currentYear);
  const isAccountConfigurationValid = useIsBookkeepingConfigurationValid();

  if (books.length === 0 || reportingFrequencies.length === 0 || !isAccountConfigurationValid) {
    return fallback ? <VATBooksEmptyState /> : <Navigate to="../books" />;
  }

  return <>{children}</>;
};

export const bookkeepingRoutes: RouteObject[] = [
  // General Bookkeeping Routes
  {
    path: 'general/transactions',
    element: (
      <UserFeaturesGuard requiredFeatures={[FeatureFlag.Bookkeeping]}>
        <BookkeepingBankTransactions />
      </UserFeaturesGuard>
    ),
  },
  {
    path: 'general/non-electronic-documents',
    element: (
      <UserFeaturesGuard requiredFeatures={[FeatureFlag.Bookkeeping]} whitelistedRoles={[UserRole.PAYROLL_CONSULTANT]}>
        <NonElectronicDocumentsPage />
      </UserFeaturesGuard>
    ),
  },
  {
    path: 'general/ledger-cards',
    element: (
      <SectionScopeProvider section="bookkeeping-ledger-cards">
        <UserFeaturesGuard requiredFeatures={[FeatureFlag.Bookkeeping]}>
          <GeneralBookkeepingLedgerCards />
        </UserFeaturesGuard>
      </SectionScopeProvider>
    ),
  },
  {
    path: 'general/cash-book',
    element: (
      <UserFeaturesGuard requiredFeatures={[FeatureFlag.Bookkeeping]}>
        <GeneralBookkeepingCashBook />
      </UserFeaturesGuard>
    ),
  },
  {
    path: 'general/provisional-balance',
    element: (
      <UserFeaturesGuard requiredFeatures={[FeatureFlag.Bookkeeping]}>
        <ProvisionalBalance />
      </UserFeaturesGuard>
    ),
  },
  {
    path: 'general/fixed-assets',
    element: (
      <UserFeaturesGuard requiredFeatures={[FeatureFlag.Bookkeeping]}>
        <FixedAssetsPage />
      </UserFeaturesGuard>
    ),
  },
  // VAT Bookkeeping Routes
  {
    path: 'books',
    element: (
      <UserFeaturesGuard requiredFeatures={[FeatureFlag.Bookkeeping]}>
        <BookkeepingGuard fallback>
          <VATBooks />
        </BookkeepingGuard>
      </UserFeaturesGuard>
    ),
  },
  {
    path: 'documents',
    element: (
      <UserFeaturesGuard requiredFeatures={[FeatureFlag.Bookkeeping]}>
        <BookkeepingGuard>
          <BookkeepingDocuments />
        </BookkeepingGuard>
      </UserFeaturesGuard>
    ),
  },
  {
    path: 'vat-settlement',
    element: (
      <UserFeaturesGuard requiredFeatures={[FeatureFlag.Bookkeeping]}>
        <BookkeepingGuard>
          <VATSettlement />
        </BookkeepingGuard>
      </UserFeaturesGuard>
    ),
  },
  {
    path: 'lipe',
    element: (
      <UserFeaturesGuard requiredFeatures={[FeatureFlag.Bookkeeping]}>
        <LIPE />
      </UserFeaturesGuard>
    ),
  },
  {
    path: 'withholding-tax',
    element: (
      <UserFeaturesGuard requiredFeatures={[FeatureFlag.Bookkeeping]}>
        <BookkeepingGuard>
          <WithholdingTaxManagement />
        </BookkeepingGuard>
      </UserFeaturesGuard>
    ),
  },
  {
    path: 'intrastat',
    element: (
      <UserFeaturesGuard requiredFeatures={[FeatureFlag.Bookkeeping]}>
        <Intrastat />
      </UserFeaturesGuard>
    ),
  },
  {
    path: 'intrastat/:declarationId/edit',
    element: (
      <UserFeaturesGuard requiredFeatures={[FeatureFlag.Bookkeeping]}>
        <EditIntrastatDeclaration />
      </UserFeaturesGuard>
    ),
  },
  {
    path: 'financial-statement',
    element: (
      <UserFeaturesGuard requiredFeatures={[FeatureFlag.Bookkeeping, FeatureFlag.BilancioCee]}>
        <FinancialStatement />
      </UserFeaturesGuard>
    ),
  },
  {
    path: 'vat-declaration',
    element: (
      <UserFeaturesGuard requiredFeatures={[FeatureFlag.Bookkeeping]}>
        <BookkeepingGuard>
          <VatDeclaration />
        </BookkeepingGuard>
      </UserFeaturesGuard>
    ),
  },
  {
    path: 'vat-declaration/:declarationId',
    element: (
      <UserFeaturesGuard requiredFeatures={[FeatureFlag.Bookkeeping]}>
        <BookkeepingGuard>
          <VatDeclarationDetailPage />
        </BookkeepingGuard>
      </UserFeaturesGuard>
    ),
  },
  {
    path: 'vat-declaration/:declarationId/:section',
    element: (
      <UserFeaturesGuard requiredFeatures={[FeatureFlag.Bookkeeping]}>
        <BookkeepingGuard>
          <VatDeclarationSectionPage />
        </BookkeepingGuard>
      </UserFeaturesGuard>
    ),
  },
  {
    path: '770',
    element: (
      <UserFeaturesGuard requiredFeatures={[FeatureFlag.Bookkeeping]}>
        <BookkeepingGuard>
          <Declaration770 />
        </BookkeepingGuard>
      </UserFeaturesGuard>
    ),
  },
  {
    path: '770/:declarationId',
    element: (
      <UserFeaturesGuard requiredFeatures={[FeatureFlag.Bookkeeping]}>
        <BookkeepingGuard>
          <Declaration770DetailPage />
        </BookkeepingGuard>
      </UserFeaturesGuard>
    ),
  },
  {
    path: '770/:declarationId/:section',
    element: (
      <UserFeaturesGuard requiredFeatures={[FeatureFlag.Bookkeeping]}>
        <BookkeepingGuard>
          <Declaration770SectionPage />
        </BookkeepingGuard>
      </UserFeaturesGuard>
    ),
  },
  {
    path: 'cu-declarations',
    element: (
      <UserFeaturesGuard requiredFeatures={[FeatureFlag.Bookkeeping]}>
        <BookkeepingGuard>
          <CUDeclarations />
        </BookkeepingGuard>
      </UserFeaturesGuard>
    ),
  },
  {
    path: 'cu-declarations/:cuDeclarationId',
    element: (
      <UserFeaturesGuard requiredFeatures={[FeatureFlag.Bookkeeping]}>
        <BookkeepingGuard>
          <CUDeclarationDetailPage />
        </BookkeepingGuard>
      </UserFeaturesGuard>
    ),
  },
  {
    path: 'cu-declarations/:cuDeclarationId/frontespizio',
    element: (
      <UserFeaturesGuard requiredFeatures={[FeatureFlag.Bookkeeping]}>
        <BookkeepingGuard>
          <CUDeclarationFrontespizioPage />
        </BookkeepingGuard>
      </UserFeaturesGuard>
    ),
  },
  {
    path: 'income-tax-declaration',
    element: (
      <UserFeaturesGuard requiredFeatures={[FeatureFlag.Bookkeeping]}>
        <BookkeepingGuard>
          <IncomeTaxDeclaration />
        </BookkeepingGuard>
      </UserFeaturesGuard>
    ),
  },
  {
    path: 'income-tax-declaration/:declarationId',
    element: (
      <UserFeaturesGuard requiredFeatures={[FeatureFlag.Bookkeeping]}>
        <BookkeepingGuard>
          <IncomeTaxDeclarationDetailPage />
        </BookkeepingGuard>
      </UserFeaturesGuard>
    ),
  },
  {
    path: 'income-tax-declaration/:declarationId/:model/:section',
    element: (
      <UserFeaturesGuard requiredFeatures={[FeatureFlag.Bookkeeping]}>
        <BookkeepingGuard>
          <IncomeTaxDeclarationSectionPage />
        </BookkeepingGuard>
      </UserFeaturesGuard>
    ),
  },
  {
    path: 'cu-declarations/:cuDeclarationId/recipients/:recipientId',
    element: (
      <UserFeaturesGuard requiredFeatures={[FeatureFlag.Bookkeeping]}>
        <BookkeepingGuard>
          <CUDeclarationRecipientRoute />
        </BookkeepingGuard>
      </UserFeaturesGuard>
    ),
  },
  {
    path: 'new-payroll-registration/:documentId',
    element: (
      <UserFeaturesGuard requiredFeatures={[FeatureFlag.Bookkeeping]}>
        <BookkeepingGuard>
          <PayrollRegistration />
        </BookkeepingGuard>
      </UserFeaturesGuard>
    ),
  },
  {
    path: 'new-receipt-registration/:documentId',
    element: (
      <UserFeaturesGuard requiredFeatures={[FeatureFlag.Bookkeeping]}>
        <BookkeepingGuard>
          <BookkeepingReceiptRegistration />
        </BookkeepingGuard>
      </UserFeaturesGuard>
    ),
  },
  {
    path: 'new-vat-registration/:documentId',
    element: (
      <UserFeaturesGuard requiredFeatures={[FeatureFlag.Bookkeeping]}>
        <BookkeepingGuard>
          <CreateVATRegistration />
        </BookkeepingGuard>
      </UserFeaturesGuard>
    ),
  },
  {
    path: 'new-registration',
    element: (
      <UserFeaturesGuard requiredFeatures={[FeatureFlag.Bookkeeping]}>
        <BookkeepingGuard>
          <CreateManualRegistration />
        </BookkeepingGuard>
      </UserFeaturesGuard>
    ),
  },
  {
    path: 'edit-vat-registration/:documentId',
    element: (
      <UserFeaturesGuard requiredFeatures={[FeatureFlag.Bookkeeping]}>
        <BookkeepingGuard>
          <EditVATRegistration />
        </BookkeepingGuard>
      </UserFeaturesGuard>
    ),
  },
  {
    path: 'edit-manual-vat-registration/:registrationId',
    element: (
      <UserFeaturesGuard requiredFeatures={[FeatureFlag.Bookkeeping]}>
        <BookkeepingGuard>
          <EditManualVATRegistration />
        </BookkeepingGuard>
      </UserFeaturesGuard>
    ),
  },
  {
    path: 'new-bills-vat-registrations/:year/:month/:source',
    element: (
      <UserFeaturesGuard requiredFeatures={[FeatureFlag.Bookkeeping]}>
        <BookkeepingGuard>
          <CreateBillsVATRegistrations />
        </BookkeepingGuard>
      </UserFeaturesGuard>
    ),
  },
  {
    path: 'debits-credits',
    element: (
      <UserFeaturesGuard requiredFeatures={[FeatureFlag.Bookkeeping]}>
        <TaxDebitCredits />
      </UserFeaturesGuard>
    ),
  },
  {
    path: 'f24',
    element: (
      <UserFeaturesGuard requiredFeatures={[FeatureFlag.Bookkeeping]}>
        <F24List />
      </UserFeaturesGuard>
    ),
  },
  {
    path: 'settings',
    element: (
      <UserFeaturesGuard requiredFeatures={[FeatureFlag.Bookkeeping]}>
        <BookkeepingSettings />
      </UserFeaturesGuard>
    ),
  },
  {
    path: 'edit-bill-vat-registration/:documentId',
    element: (
      <UserFeaturesGuard requiredFeatures={[FeatureFlag.Bookkeeping]}>
        <BookkeepingGuard>
          <EditBillVATRegistration />
        </BookkeepingGuard>
      </UserFeaturesGuard>
    ),
  },
  {
    path: '*',
    element: <Navigate to="../general/transactions" />,
  },
];
